import { getItem, type InventoryState, type ItemInstance, type ItemTemplate } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { section } from './bench.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { uiFormat, uiText } from './text.js';

/**
 * The pieces a craftsman works on (the smith, the runesmith): worn first,
 * then the bag in slot order, shown as a grid of slots under « Portées » and
 * « Dans ton sac ». Each slot is a `.smith__piece` with the template in
 * `data-template`: the camp's coach points at one.
 */

export interface Piece {
  instance: ItemInstance;
  worn: boolean;
}

export function piecesOf(
  inventory: InventoryState | undefined,
  keep: (instance: ItemInstance, template: ItemTemplate) => boolean,
): Piece[] {
  if (inventory === undefined) return [];
  const kept = (instance: ItemInstance): boolean => {
    const template = getItem(instance.templateId);
    return template !== undefined && keep(instance, template);
  };
  return [
    ...Object.values(inventory.equipped)
      .filter(kept)
      .map((instance) => ({ instance, worn: true })),
    ...inventory.bag
      .map((entry) => entry.item)
      .filter(kept)
      .map((instance) => ({ instance, worn: false })),
  ];
}

export interface PieceListOptions {
  icons: CssAtlas | null;
  selectedId: string | undefined;
  /** What the list says when nothing qualifies. */
  empty: string;
  onPick: (piece: Piece) => void;
}

export function fillPieceList(
  list: HTMLElement,
  pieces: readonly Piece[],
  options: PieceListOptions,
): void {
  list.replaceChildren();
  const worn = pieces.filter((piece) => piece.worn);
  const carried = pieces.filter((piece) => !piece.worn);
  // Headings only when both groups show: one group needs no name.
  const headed = worn.length > 0 && carried.length > 0;
  if (headed) list.append(group(uiText('Portées')));
  for (const piece of worn) list.append(cell(piece, options));
  if (headed) list.append(group(uiText('Dans ton sac')));
  for (const piece of carried) list.append(cell(piece, options));
  if (pieces.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'smith__muted';
    empty.textContent = options.empty;
    list.append(empty);
  }
}

/** A heading across the grid of pieces. */
function group(label: string): HTMLElement {
  const heading = section(label);
  heading.classList.add('smith__group');
  return heading;
}

function cell(piece: Piece, options: PieceListOptions): HTMLElement {
  const template = getItem(piece.instance.templateId);
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'slot smith__piece';
  button.dataset.template = piece.instance.templateId;
  if (piece.instance.id === options.selectedId) button.classList.add('smith__piece--selected');
  const plus = piece.instance.upgradeLevel > 0 ? ` +${String(piece.instance.upgradeLevel)}` : '';
  button.title = uiFormat(piece.worn ? '{name}{plus} (porté)' : '{name}{plus}', {
    name: uiText(template?.name ?? ''),
    plus,
  });
  const item = document.createElement('div');
  item.className = `item item--${template?.rarity ?? 'common'}`;
  paintItemIcon(item, template, options.icons, piece.instance.templateId);
  addLevelBadge(item, piece.instance.upgradeLevel);
  button.append(item);
  button.addEventListener('click', () => {
    options.onPick(piece);
  });
  return button;
}
